import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';

import {
  createTagAssignment,
  createUserTag,
  deleteTagAssignment,
  deleteUserTag,
  getTagsForSource,
  getUserTagAssignments,
  getUserTags,
} from './api';


function positiveInteger(value) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

export function normalizeTagSource(source) {
  const targetType = source?.targetType || source?.favoriteType || null;
  const sourceId = positiveInteger(source?.sourceId);

  if (!['session', 'question', 'clip'].includes(targetType) || !sourceId) {
    return null;
  }

  return {
    ...source,
    targetType,
    sourceId,
    key: `${targetType}:${sourceId}`,
  };
}

function targetLabel(targetType) {
  if (targetType === 'question') return 'question / answer';
  if (targetType === 'clip') return 'transcript result';
  return 'session';
}

function assignmentTypeLabel(targetType) {
  if (targetType === 'question') return 'Question';
  if (targetType === 'clip') return 'Transcript excerpt';
  return 'Session';
}

function formatTimestamp(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '';

  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remainder = whole % 60;

  return [hours, minutes, remainder]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

function TagIcon({ active = false }) {
  return (
    <svg
      className="tag-page-icon"
      viewBox="0 0 24 24"
      width="19"
      height="19"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3.5 5.5v5.15c0 .8.32 1.57.88 2.13l7.34 7.34a2 2 0 0 0 2.83 0l5.57-5.57a2 2 0 0 0 0-2.83L12.78 4.38a3 3 0 0 0-2.13-.88H5.5a2 2 0 0 0-2 2Z"
        fill={active ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="1.35" fill={active ? '#0f172a' : 'currentColor'} />
    </svg>
  );
}

export function TagButton({
  source,
  status = null,
  checking = false,
  onChanged = null,
}) {
  const normalizedSource = normalizeTagSource(source);
  const [opened, setOpened] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const [changingTagId, setChangingTagId] = useState(null);

  const statusCount = Number(status?.tagCount ?? status?.tag_count ?? 0);
  const assignedCount = opened
    ? items.filter((item) => item.assigned).length
    : (Number.isFinite(statusCount) ? statusCount : 0);

  async function loadSourceTags() {
    if (!normalizedSource) return;

    setLoading(true);
    setError('');

    try {
      const data = await getTagsForSource({
        targetType: normalizedSource.targetType,
        sourceId: normalizedSource.sourceId,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err.message || 'Unable to load tags.');
    } finally {
      setLoading(false);
    }
  }

  function openTags(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setOpened(true);
  }

  useEffect(() => {
    if (opened) {
      loadSourceTags();
    }
  }, [opened, normalizedSource?.key]);

  async function toggleTag(tag) {
    if (!normalizedSource || changingTagId !== null) return;

    setChangingTagId(tag.id);
    setError('');

    try {
      if (tag.assigned && tag.assignment_id) {
        await deleteTagAssignment({ assignmentId: tag.assignment_id });
        setItems((current) => current.map((item) => (
          item.id === tag.id
            ? {
                ...item,
                assigned: false,
                assignment_id: null,
                assignment_count: Math.max(0, Number(item.assignment_count || 0) - 1),
              }
            : item
        )));
      } else {
        const data = await createTagAssignment({
          tagId: tag.id,
          targetType: normalizedSource.targetType,
          sourceId: normalizedSource.sourceId,
          sourceQuery: normalizedSource.sourceQuery || null,
        });
        const assignmentId = positiveInteger(data?.assignment?.id);
        setItems((current) => current.map((item) => (
          item.id === tag.id
            ? {
                ...item,
                assigned: true,
                assignment_id: assignmentId,
                assignment_count: Number(item.assignment_count || 0) + (data?.created === false ? 0 : 1),
              }
            : item
        )));
      }

      onChanged?.();
    } catch (err) {
      setError(err.message || 'Unable to update tag.');
    } finally {
      setChangingTagId(null);
    }
  }

  async function createAndAssignTag() {
    const cleanedName = String(newTagName || '').trim();
    if (!normalizedSource || !cleanedName || creating) return;

    setCreating(true);
    setError('');

    try {
      const tagData = await createUserTag({ name: cleanedName });
      const tag = tagData?.tag;
      const tagId = positiveInteger(tag?.id);

      if (!tagId) {
        throw new Error('TD Classes did not return the new tag.');
      }

      const assignmentData = await createTagAssignment({
        tagId,
        targetType: normalizedSource.targetType,
        sourceId: normalizedSource.sourceId,
        sourceQuery: normalizedSource.sourceQuery || null,
      });

      const assignmentId = positiveInteger(assignmentData?.assignment?.id);
      const nextItem = {
        ...tag,
        assigned: true,
        assignment_id: assignmentId,
        assignment_count:
          Number(tag?.assignment_count || 0) + (assignmentData?.created === false ? 0 : 1),
      };

      setItems((current) => {
        const withoutSameTag = current.filter((item) => item.id !== tagId);
        return [...withoutSameTag, nextItem].sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
        );
      });
      setNewTagName('');
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Unable to create tag.');
    } finally {
      setCreating(false);
    }
  }

  if (!normalizedSource) return null;

  const label = checking
    ? 'Checking tags…'
    : assignedCount > 0
      ? `Manage tags (${assignedCount})`
      : 'Add or manage tags';

  return (
    <>
      <Tooltip label={label} withArrow position="top" openDelay={250}>
        <button
          type="button"
          className={`tag-toggle-button${assignedCount > 0 ? ' tag-toggle-button-active' : ''}`}
          aria-label={label}
          disabled={checking}
          onClick={openTags}
        >
          {checking ? <Loader size={16} /> : <TagIcon active={assignedCount > 0} />}
          {assignedCount > 0 && <span className="tag-toggle-count">{assignedCount}</span>}
        </button>
      </Tooltip>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={`Tags for this ${targetLabel(normalizedSource.targetType)}`}
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Apply any number of your personal tags. Tags are private to your signed-in account.
          </Text>

          {error && (
            <Alert color="red" title="Tag error">
              {error}
            </Alert>
          )}

          <Group align="flex-end" wrap="nowrap">
            <TextInput
              label="Create a new tag"
              placeholder="e.g. CRT, IB DOL, Review Again"
              value={newTagName}
              maxLength={100}
              style={{ flex: 1 }}
              onChange={(event) => setNewTagName(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  createAndAssignTag();
                }
              }}
            />
            <Button
              disabled={!String(newTagName || '').trim() || creating}
              loading={creating}
              onClick={createAndAssignTag}
            >
              Create & apply
            </Button>
          </Group>

          {loading && (
            <Group justify="center" py="md">
              <Loader />
            </Group>
          )}

          {!loading && items.length === 0 && (
            <Paper withBorder radius="md" p="md" className="tag-empty-state">
              <Text fw={700}>You have not created any tags yet.</Text>
              <Text size="sm" c="dimmed" mt={4}>
                Create one above and it will be reusable anywhere TD Classes shows a tag button.
              </Text>
            </Paper>
          )}

          {!loading && items.length > 0 && (
            <Stack gap={4}>
              <Text size="sm" fw={700}>Your tags</Text>
              {items.map((tag) => (
                <Paper key={tag.id} withBorder radius="sm" p="xs" className="tag-choice-row">
                  <Checkbox
                    checked={Boolean(tag.assigned)}
                    disabled={changingTagId !== null}
                    label={(
                      <Group gap="xs" wrap="wrap">
                        <Text size="sm" fw={600}>{tag.name}</Text>
                        <Badge size="xs" variant="light">
                          {Number(tag.assignment_count || 0)} item{Number(tag.assignment_count || 0) === 1 ? '' : 's'}
                        </Badge>
                      </Group>
                    )}
                    onChange={() => toggleTag(tag)}
                  />
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Modal>
    </>
  );
}


function AssignmentCard({ assignment, removing = false, onOpen, onRemove = null }) {
  const startTime = formatTimestamp(assignment.start_seconds);
  const canOpen = Boolean(assignment.live_source_available)
    && (assignment.target_type === 'session' || Boolean(startTime));

  return (
    <Paper withBorder radius="md" p="sm" className="tag-assignment-card">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div style={{ minWidth: 0 }}>
            <Group gap="xs" wrap="wrap">
              <Badge variant="light">{assignmentTypeLabel(assignment.target_type)}</Badge>
              {startTime && <Badge variant="outline">{startTime}</Badge>}
              <Badge variant="outline">{assignment.session_date_snapshot}</Badge>
            </Group>
            <Text fw={700} mt={5}>{assignment.title_snapshot}</Text>
            <Text size="sm" c="dimmed" mt={2}>{assignment.session_title_snapshot}</Text>
          </div>

          <Group gap="xs" wrap="nowrap">
            <Button
              size="xs"
              variant="light"
              disabled={!canOpen || removing}
              onClick={() => onOpen(assignment)}
            >
              {assignment.target_type === 'session' ? 'Open session' : 'Open transcript'}
            </Button>
            {onRemove && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                disabled={removing}
                onClick={() => onRemove(assignment)}
              >
                {removing ? 'Removing…' : 'Remove'}
              </Button>
            )}
          </Group>
        </Group>

        {assignment.snippet_snapshot && (
          <Text size="sm" className="tag-assignment-snippet" lineClamp={4}>
            {assignment.snippet_snapshot}
          </Text>
        )}

        {!assignment.live_source_available && (
          <Text size="xs" c="yellow.4">
            The original source is no longer available. TD Classes retained the saved tag snapshot.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}


export function SessionTagSummary({
  summary = null,
  sessionTitle = '',
  onOpenAssignment,
}) {
  const [openedTag, setOpenedTag] = useState(null);
  const tags = Array.isArray(summary?.tags) ? summary.tags : [];

  if (tags.length === 0) {
    return null;
  }

  function openTag(tag) {
    const assignments = Array.isArray(tag?.assignments) ? tag.assignments : [];

    if (assignments.length === 1) {
      onOpenAssignment?.(assignments[0]);
      return;
    }

    setOpenedTag(tag);
  }

  const openedAssignments = Array.isArray(openedTag?.assignments)
    ? openedTag.assignments
    : [];

  return (
    <>
      <Group gap={6} align="center" wrap="wrap" className="session-personal-tags-row">
        <Text size="xs" fw={700} className="session-personal-tags-label">
          MY TAGS
        </Text>

        {tags.map((tag) => {
          const assignmentCount = Number(tag.assignment_count || tag.assignments?.length || 0);
          const title = assignmentCount === 1
            ? `Open the tagged location for ${tag.name}`
            : `Choose from ${assignmentCount} tagged locations for ${tag.name}`;

          return (
            <Tooltip key={tag.tag_id} label={title} withArrow position="top" openDelay={250}>
              <button
                type="button"
                className="session-personal-tag-chip"
                onClick={() => openTag(tag)}
              >
                <span>{tag.name}</span>
                {assignmentCount > 1 && (
                  <span className="session-personal-tag-count">{assignmentCount}</span>
                )}
              </button>
            </Tooltip>
          );
        })}
      </Group>

      <Modal
        opened={Boolean(openedTag)}
        onClose={() => setOpenedTag(null)}
        title={openedTag ? `${openedTag.name} in ${sessionTitle || 'this session'}` : 'Tagged locations'}
        size="lg"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            This tag appears in more than one place in the session. Choose the location you want to open.
          </Text>

          {openedAssignments.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              onOpen={(selectedAssignment) => {
                setOpenedTag(null);
                onOpenAssignment?.(selectedAssignment);
              }}
            />
          ))}
        </Stack>
      </Modal>
    </>
  );
}


export function MyTagsModal({
  opened,
  onClose,
  onOpenAssignment,
  onChanged = null,
}) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [openedTags, setOpenedTags] = useState([]);
  const [assignmentsByTag, setAssignmentsByTag] = useState({});
  const [loadingTagId, setLoadingTagId] = useState(null);
  const [deletingTagId, setDeletingTagId] = useState(null);
  const [removingAssignmentId, setRemovingAssignmentId] = useState(null);

  async function loadTags() {
    setLoading(true);
    setError('');

    try {
      const data = await getUserTags();
      setTags(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err.message || 'Unable to load tags.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      setOpenedTags([]);
      setAssignmentsByTag({});
      loadTags();
    }
  }, [opened]);

  async function loadAssignments(tagId, { force = false } = {}) {
    if (!force && assignmentsByTag[tagId]) return;

    setLoadingTagId(tagId);
    setError('');

    try {
      const data = await getUserTagAssignments({ tagId });
      setAssignmentsByTag((current) => ({
        ...current,
        [tagId]: Array.isArray(data?.items) ? data.items : [],
      }));
    } catch (err) {
      setError(err.message || 'Unable to load tagged items.');
    } finally {
      setLoadingTagId(null);
    }
  }

  function handleAccordionChange(values) {
    const next = Array.isArray(values) ? values : [];
    setOpenedTags(next);

    for (const value of next) {
      const tagId = positiveInteger(value);
      if (tagId && !assignmentsByTag[tagId]) {
        loadAssignments(tagId);
      }
    }
  }

  async function removeAssignment(assignment) {
    if (!assignment?.id || removingAssignmentId !== null) return;

    setRemovingAssignmentId(assignment.id);
    setError('');

    try {
      await deleteTagAssignment({ assignmentId: assignment.id });
      setAssignmentsByTag((current) => ({
        ...current,
        [assignment.tag_id]: (current[assignment.tag_id] || []).filter(
          (item) => item.id !== assignment.id
        ),
      }));
      setTags((current) => current.map((tag) => (
        tag.id === assignment.tag_id
          ? { ...tag, assignment_count: Math.max(0, Number(tag.assignment_count || 0) - 1) }
          : tag
      )));
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Unable to remove tag from item.');
    } finally {
      setRemovingAssignmentId(null);
    }
  }

  async function removeTag(tag) {
    if (!tag?.id || deletingTagId !== null) return;

    const count = Number(tag.assignment_count || 0);
    const confirmed = window.confirm(
      count > 0
        ? `Delete the tag “${tag.name}” and remove it from ${count} tagged item${count === 1 ? '' : 's'}?`
        : `Delete the tag “${tag.name}”?`
    );

    if (!confirmed) return;

    setDeletingTagId(tag.id);
    setError('');

    try {
      await deleteUserTag({ tagId: tag.id });
      setTags((current) => current.filter((item) => item.id !== tag.id));
      setAssignmentsByTag((current) => {
        const next = { ...current };
        delete next[tag.id];
        return next;
      });
      setOpenedTags((current) => current.filter((value) => Number(value) !== Number(tag.id)));
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Unable to delete tag.');
    } finally {
      setDeletingTagId(null);
    }
  }

  const filteredTags = useMemo(() => {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return tags;
    return tags.filter((tag) => String(tag.name || '').toLowerCase().includes(needle));
  }, [tags, query]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="My Tags"
      size="xl"
      centered
      styles={{ body: { maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' } }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Your tags are private. Expand a tag to browse everything you assigned to it, jump back to the source, or remove individual assignments.
        </Text>

        <TextInput
          label="Find a tag"
          placeholder="Search tag names..."
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />

        {error && (
          <Alert color="red" title="Tags error">
            {error}
          </Alert>
        )}

        {loading && (
          <Group justify="center" py="xl"><Loader /></Group>
        )}

        {!loading && tags.length === 0 && (
          <Paper withBorder radius="md" p="lg" className="tag-empty-state">
            <Text fw={700}>No tags yet.</Text>
            <Text size="sm" c="dimmed" mt={4}>
              Use a tag button beside a session, question, or search result to create your first tag.
            </Text>
          </Paper>
        )}

        {!loading && tags.length > 0 && filteredTags.length === 0 && (
          <Text size="sm" c="dimmed">No tag names match that search.</Text>
        )}

        {!loading && filteredTags.length > 0 && (
          <Accordion
            multiple
            variant="separated"
            value={openedTags}
            onChange={handleAccordionChange}
          >
            {filteredTags.map((tag) => {
              const assignments = assignmentsByTag[tag.id] || null;
              const isLoadingAssignments = loadingTagId === tag.id;

              return (
                <Accordion.Item key={tag.id} value={String(tag.id)}>
                  <Accordion.Control>
                    <Group justify="space-between" pr="md" wrap="nowrap">
                      <Text fw={700}>{tag.name}</Text>
                      <Badge variant="light">
                        {Number(tag.assignment_count || 0)} item{Number(tag.assignment_count || 0) === 1 ? '' : 's'}
                      </Badge>
                    </Group>
                  </Accordion.Control>

                  <Accordion.Panel>
                    <Stack gap="sm">
                      <Group justify="flex-end">
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          disabled={deletingTagId !== null || removingAssignmentId !== null}
                          onClick={() => removeTag(tag)}
                        >
                          {deletingTagId === tag.id ? 'Deleting…' : 'Delete entire tag'}
                        </Button>
                      </Group>

                      {isLoadingAssignments && (
                        <Group justify="center" py="md"><Loader size="sm" /></Group>
                      )}

                      {!isLoadingAssignments && assignments && assignments.length === 0 && (
                        <Text size="sm" c="dimmed">This tag is not currently assigned to anything.</Text>
                      )}

                      {!isLoadingAssignments && assignments && assignments.map((assignment) => (
                        <AssignmentCard
                          key={assignment.id}
                          assignment={assignment}
                          removing={removingAssignmentId === assignment.id}
                          onOpen={onOpenAssignment}
                          onRemove={removeAssignment}
                        />
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Stack>
    </Modal>
  );
}
